import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { type AuthUser, type ConfigurazioneMfa, mfaConfirm, mfaSetup } from "../auth/api";
import { useResource } from "./api";

const BTN =
  "rounded-md border border-accent bg-accent-2 px-3.5 py-2 text-[13px] font-medium text-white " +
  "hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft " +
  "disabled:opacity-60";

const FIELD =
  "mt-1 h-11 w-full rounded-md border border-line bg-paper px-3 text-[13px] text-ink " +
  "focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft";

/**
 * Card "Accesso a due fattori" (Impostazioni, solo web-mode).
 *
 * L'arruolamento è in due tempi di proposito: il codice segreto si genera,
 * ma il secondo fattore si accende SOLO dopo che l'utente ha digitato un
 * codice giusto. Accenderlo subito chiuderebbe fuori chi ha sbagliato a
 * copiare la chiave — e a quel punto servirebbe un altro amministratore per
 * rientrare.
 *
 * QR prima, chiave dopo: il QR codifica l'URI ``otpauth://`` che il backend
 * restituisce già (``config.uri``) e si inquadra col telefono senza copiare
 * nulla; la chiave leggibile resta sotto come alternativa, per chi non ha la
 * fotocamera a portata o usa un'app sul computer. Il QR si disegna in locale
 * (``qrcode.react``): il segreto non esce mai verso un servizio di terzi.
 */
export function MfaCard({ onAttivata }: { onAttivata?: () => void } = {}) {
  const me = useResource<{ user: AuthUser } | null>("/api/auth/me", null);
  const [config, setConfig] = useState<ConfigurazioneMfa | null>(null);
  const [codice, setCodice] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attivata, setAttivata] = useState(false);

  const attiva = attivata || Boolean(me.value?.user.mfa_attiva);

  async function avvia(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setConfig(await mfaSetup(password));
      setPassword("");
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function conferma(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await mfaConfirm(codice);
      setAttivata(true);
      onAttivata?.();
      setConfig(null);
      setCodice("");
    } catch (err) {
      setError(prettifyApiError(err));
      setCodice("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="px-5 py-4 max-md:px-4">
      <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">Accesso a due fattori</h3>
      <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2">
        Un codice di sei cifre dall'app del telefono, oltre alla password. Serve una sola volta ogni
        volta che la sessione scade.
      </p>

      <ErrorBanner message={error} className="mt-4 block" />

      {attiva ? (
        <p className="mt-4 rounded-full border border-line bg-paper px-3.5 py-3 text-[13px] text-ink-2">
          <span className="font-medium text-ink">Attivo su questo account.</span> Se cambi telefono,
          chiedi a un amministratore di azzerarlo: da lì si riparte da capo.
        </p>
      ) : config ? (
        <form onSubmit={(event) => void conferma(event)} className="mt-4">
          <p className="text-[12.5px] text-ink-2">Apri l'app di autenticazione e inquadra il QR:</p>
          <div className="mt-2 inline-block rounded-lg border border-line bg-card p-3">
            <QRCodeSVG
              value={config.uri}
              size={176}
              marginSize={0}
              role="img"
              aria-label="QR code per l'app di autenticazione"
            />
          </div>
          <p className="mt-3 text-[12.5px] text-ink-2">
            Oppure scegli «inserisci una chiave» e copia questa:
          </p>
          <p className="mt-2 select-all rounded-lg border border-line bg-paper px-3.5 py-3 tabular-nums text-[15px] tracking-wider text-ink">
            {config.secret_leggibile}
          </p>
          <p className="mt-2 text-[11.5px] leading-snug text-ink-2">
            Nome account: Order Manager. La chiave si vede solo adesso — se chiudi la pagina prima
            di confermare, si ricomincia.
          </p>

          <label htmlFor="mfa-codice" className="mt-4 block text-[12.5px] font-medium text-ink-2">
            Codice mostrato dall'app
          </label>
          <input
            id="mfa-codice"
            type="text"
            value={codice}
            onChange={(event) => setCodice(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            required
            disabled={busy}
            className={`${FIELD} tabular-nums tracking-[0.3em]`}
          />
          <button type="submit" disabled={busy} className={`mt-3 ${BTN}`}>
            {busy ? "Verifica…" : "Attiva"}
          </button>
        </form>
      ) : (
        <form onSubmit={(event) => void avvia(event)} className="mt-4">
          <label htmlFor="mfa-password" className="block text-[12.5px] font-medium text-ink-2">
            Password attuale
          </label>
          <input
            id="mfa-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            disabled={busy}
            className={FIELD}
          />
          <button type="submit" disabled={busy} className={`mt-3 ${BTN}`}>
            {busy ? "Attendere…" : "Attiva il secondo fattore"}
          </button>
        </form>
      )}
    </section>
  );
}
